/* eslint-disable import/first */

if (process.env.NODE_ENV === 'development') {
  console.info('Development mode: enabling Preact debug tools');
  require('preact/debug');
}

import type { Player } from '@schibsted-svp/web-player';
import type { StreamProvider } from '@vgtv/api-client';
import cn from 'classnames';
import { FunctionComponent } from 'preact';
import { createPortal } from 'preact/compat';

import { SchibstedAccount } from '../../config';
import { isTouchDevice } from '../../utils/device';
import LocaleProvider from '../../utils/LocaleProvider';
import { getLocale } from '../../utils/player/config';
import { SchibstedAccountProvider } from '../../utils/SchibstedAccountProvider';

import { AccessDefinitionsProvider } from './hooks/AccessDefinitionsProvider';
import { ApisProvider } from './hooks/ApisProvider';
import { CastProvider } from './hooks/CastProvider';
import { Config, ConfigProvider, useConfig } from './hooks/ConfigProvider';
import {
  CurrentAssetProvider,
  useCurrentAsset,
} from './hooks/CurrentAssetProvider';
import { ErrorRecoveryProvider } from './hooks/ErrorRecoveryProvider';
import { ExperimentsProvider } from './hooks/ExperimentsProvider';
import { FloatingProvider } from './hooks/FloatingProvider';
import { GeolocationProvider } from './hooks/GeolocationProvider';
import { NextAssetProvider } from './hooks/NextAssetProvider/NextAssetProvider';
import { PaywallProvider, PaywallProviderProps } from './hooks/PaywallProvider';
import { ErrorDetails, PlayerErrorProvider } from './hooks/PlayerErrorProvider';
import { PlayerProvider, usePlayer } from './hooks/PlayerProvider';
import { PulseProvider } from './hooks/PulseProvider';
import { SeriesProvider } from './hooks/SeriesProvider';
import { useAssetIdCopy } from './hooks/useAssetIdCopy';
import { useChromecastTracking } from './hooks/useChromecastTracking';
import { usePlayerContainer } from './hooks/usePlayerContainer';
import { useQualityChangeTracking } from './hooks/useQualityChangeTracking';
import { useSkinContainerDecorations } from './hooks/useSkinContainerDecorations';
import { useViewportType } from './hooks/useViewportType';
import { AudioLayoutResolver, VideoLayoutResolver } from './layouts';
import { isAudioContent } from './utils/isAudioContent';

export interface SkinProps {
  player: Player;
  initialError?: ErrorDetails;
  castState?: jwplayer.CastParam;
  paywall: PaywallProviderProps;
  schibstedAccount: SchibstedAccount;
  config: Config;
}

const App = () => {
  const { handleContextMenu } = useAssetIdCopy();

  useChromecastTracking();
  useQualityChangeTracking();

  const { viewport } = useViewportType();
  const player = usePlayer();
  const { dnaStyle, podcast } = useConfig();
  const { asset } = useCurrentAsset();
  const container = usePlayerContainer();
  const isAudio = isAudioContent(asset);
  const { mode, theme, dnaClass, audioContainer } = useSkinContainerDecorations(
    {
      container,
      vendor: player.getRawConfig().vendor as StreamProvider,
      dnaStyle,
      isAudio,
    }
  );

  if (isAudio && audioContainer) {
    return createPortal(
      <div
        onContextMenu={handleContextMenu}
        className={cn('vgp-skin', 'vgp-audio', 'vgp-player', dnaClass, {
          'vgp-no-touch': !isTouchDevice,
          'vgp-podcast-player-only': podcast?.variant === 'player-only',
        })}
        data-theme={theme}
        data-color-scheme={mode}
      >
        <AudioLayoutResolver />
      </div>,
      audioContainer
    );
  }

  return (
    <div
      onContextMenu={handleContextMenu}
      className={cn('vgp-skin', 'vgp-player', `vgp-${viewport}`, {
        'vgp-no-touch': !isTouchDevice,
      })}
      data-theme={theme}
      data-color-scheme={mode}
    >
      <VideoLayoutResolver />
    </div>
  );
};

export const Skin: FunctionComponent<SkinProps> = ({
  player,
  initialError,
  castState,
  paywall,
  config,
  schibstedAccount,
}) => {
  return (
    <SchibstedAccountProvider schibstedAccount={schibstedAccount}>
      <GeolocationProvider>
        <ConfigProvider config={config}>
          <LocaleProvider locale={getLocale(player.getRawConfig())}>
            <PlayerProvider value={player}>
              <CastProvider state={castState}>
                <CurrentAssetProvider>
                  <ExperimentsProvider>
                    <PulseProvider>
                      <PlayerErrorProvider initialError={initialError}>
                        <ErrorRecoveryProvider>
                          <ApisProvider>
                            <AccessDefinitionsProvider>
                              <NextAssetProvider>
                                <PaywallProvider
                                  login={paywall.login}
                                  utmParameters={paywall.utmParameters}
                                  getSubscriptionUrl={
                                    paywall.getSubscriptionUrl
                                  }
                                >
                                  <SeriesProvider>
                                    <FloatingProvider>
                                      <App />
                                    </FloatingProvider>
                                  </SeriesProvider>
                                </PaywallProvider>
                              </NextAssetProvider>
                            </AccessDefinitionsProvider>
                          </ApisProvider>
                        </ErrorRecoveryProvider>
                      </PlayerErrorProvider>
                    </PulseProvider>
                  </ExperimentsProvider>
                </CurrentAssetProvider>
              </CastProvider>
            </PlayerProvider>
          </LocaleProvider>
        </ConfigProvider>
      </GeolocationProvider>
    </SchibstedAccountProvider>
  );
};
